import React from "react";
import { motion } from "motion/react";
import {
  User,
  Mail,
  Phone,
  Shield,
  Edit3,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
} from "lucide-react";

const getTotalPermissions = (permissions) => {
  if (!permissions || typeof permissions !== "object") return 0;
  return Object.values(permissions).reduce((total, modulePermissions) => {
    return (
      total + (Array.isArray(modulePermissions) ? modulePermissions.length : 0)
    );
  }, 0);
};

const AdminTable = ({
  admins,
  onEdit,
  onDelete,
  onViewPermissions,
  onToggleStatus,
}) => {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <table className="w-full min-w-[800px] border-collapse">
        <thead>
          <tr className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              Administrator
            </th>
            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              Contact
            </th>
            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              Role
            </th>
            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              Status
            </th>
            <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              Permissions
            </th>
            <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {admins.map((admin, index) => {
            const isActive =
              admin.status === true ||
              admin.status === 1 ||
              admin.status === "1" ||
              admin.status === "active";
            const totalPermissions = getTotalPermissions(admin.permissions);

            return (
              <motion.tr
                key={admin.admin_slug || index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                className="group bg-white hover:bg-gray-50/50 transition-colors"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {admin.first_name} {admin.last_name}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">
                        {admin.admin_slug?.slice(0, 8)}...
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate max-w-[200px]">{admin.email}</span>
                    </div>
                    {admin.phone_number && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span>{admin.phone_number}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">
                      {admin.role || "Administrator"}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ring-1 ring-black/5 ${
                      isActive
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-amber-50 text-amber-700 ring-amber-200"
                    }`}
                  >
                    {isActive ? (
                      <>
                        <CheckCircle className="h-3.5 w-3.5" /> Active
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3.5 w-3.5" /> Inactive
                      </>
                    )}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className="inline-flex items-center rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-200/80">
                    {totalPermissions} permission{totalPermissions !== 1 ? "s" : ""}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onToggleStatus(admin)}
                      className={`rounded-lg p-2 transition-colors ${
                        isActive
                          ? "text-red-600 hover:bg-red-50"
                          : "text-emerald-600 hover:bg-emerald-50"
                      }`}
                      title={isActive ? "Disapprove" : "Approve"}
                    >
                      {isActive ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onViewPermissions(admin)}
                      className="rounded-lg p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      title="View permissions"
                    >
                      <Eye className="h-4 w-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onEdit(admin)}
                      className="rounded-lg p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="h-4 w-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => onDelete(admin)}
                      className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </motion.button>
                  </div>
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AdminTable;
